import { useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError, get, type DestinationInput, type DestProtocol, type Ingest, type Profile, type StreamKey } from '../../api'
import { platformOf } from '../adapter'
import { GraceSettings } from '../GraceSettings'
import { ProtocolSettings, choiceOf, urlErrorFor } from '../ProtocolSettings'
import { domainOf, portrait, profileSteps, serverFromPlatform } from '../setup'
import { Banner, Field, LogoSlot, PrimaryButton, SecondaryButton } from '../ui'
import { t } from '../../i18n'
import { Icon, Tag } from '../../ui'
import { Link } from 'react-router'
import { useSession } from '../../session'
import { BILLING_PATH, refusalText } from '../../billing'

const label = (p: Profile) => (p.id === 'custom' ? t('add.customName') : p.name)

/** `9:16` after the name of a portrait platform (.sx-tag, VT-11: the main stream has no tag). DS: VT-21 */
function PortraitTag({ p }: { p: Profile }) {
  if (!portrait(p)) return null
  return <Tag data-testid={`add-tag-${p.id}`} title={t('add.portrait', { platform: label(p) })}>{t('vert.tag')}</Tag>
}

/** The catalog: every profile with its name next to the logo (finding N1), searchable. */
export function Catalog({ profiles, onPick, onDual }: { profiles?: Profile[]; onPick: (p: Profile) => void;
  /** VERT-1: open the YouTube Dual stream wizard (a profile with dual youtube_second_key) */
  onDual?: () => void }) {
  const [q, setQ] = useState('')
  const s = q.trim().toLowerCase()
  const list = (profiles ?? []).filter((p) => !s || p.name.toLowerCase().includes(s) || p.id.includes(s))
  return (
    <div data-testid="add-catalog" className="flex flex-col gap-3">
      <label className="flex min-h-9 items-center gap-1.5 rounded-md border border-line-2 px-2 text-[13px] text-muted focus-within:border-accent max-md:min-h-[44px]">
        <span className="sr-only">{t('add.search')}</span>
        <input data-testid="add-search" type="search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('add.search')}
          className="w-full bg-transparent text-text outline-none placeholder:text-muted" />
      </label>
      {!profiles && <p className="m-0 text-[13px] text-muted">{t('add.loading')}</p>}
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {/* VERT-1 (docs/vertical.md §4.1): both YouTube streams of one broadcast, from the wizard */}
        {onDual && (profiles ?? []).some((p) => p.dual === 'youtube_second_key' && (!s || p.name.toLowerCase().includes(s) || 'dual'.includes(s))) && (
          <li>
            <button type="button" data-testid="add-pick-youtube-dual" onClick={onDual}
              className="flex min-h-12 w-full items-center gap-2.5 rounded-md border border-line bg-panel px-2.5 text-left hover:border-accent focus-visible:shadow-focus">
              <LogoSlot platform="youtube" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex items-center gap-1.5 truncate text-sm font-medium">{t('vert.yt.title')}</span>
                <span className="truncate text-[11px] text-muted">{t('vert.dual.sub')}</span>
              </span>
              <Icon name="chevron-right" size={16} className="text-muted" />
            </button>
          </li>
        )}
        {list.map((p) => (
          <li key={p.id}>
            <button type="button" data-testid={`add-pick-${p.id}`} onClick={() => onPick(p)}
              className="flex min-h-12 w-full items-center gap-2.5 rounded-md border border-line bg-panel px-2.5 text-left hover:border-accent focus-visible:shadow-focus">
              <LogoSlot platform={platformOf(p.id)} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex min-w-0 items-center gap-1.5"><span className="truncate text-sm font-medium">{label(p)}</span><PortraitTag p={p} /></span>
                <span className="truncate text-[11px] text-muted">
                  {p.id === 'custom' ? t('add.customSub') : serverFromPlatform(p) ? t('add.subServerKey', { platform: label(p) }) : p.key_per_session ? t('add.keyEveryLive') : p.go_live_note ? t('add.goLiveThere') : t('add.keySub', { site: domainOf(p.key_url) || p.name })}
                </span>
              </span>
              <Icon name="chevron-right" size={16} className="text-muted" />
            </button>
          </li>
        ))}
        {profiles && list.length === 0 && <li className="text-[13px] text-muted">{t('add.none')}</li>}
      </ul>
    </div>
  )
}

/**
 * The form of the picked platform: name, server (filled for the platform), stream key
 * (optional: the output waits with needs_key), where to get the key, Advanced (the
 * protocol where the platform offers a choice — YouTube RTMPS / HLS — and the hold when
 * OBS drops). Live: "Add and go live", the others keep streaming.
 */
export function AddForm({ profile: p, live, count, max, onCancel, onCreate }: {
  profile: Profile; live: boolean; count: number; max: number;
  onCancel: () => void; onCreate: (body: DestinationInput) => Promise<void>;
}) {
  const [name, setName] = useState(label(p))
  const [url, setUrl] = useState(p.url ?? '')
  const [key, setKey] = useState('')
  const [shown, setShown] = useState(false)
  const [advanced, setAdvanced] = useState(false)
  const [grace, setGrace] = useState<number | null>(null)
  const [graceBad, setGraceBad] = useState(false)
  const protocols = choiceOf(p)
  const [protocol, setProtocol] = useState<DestProtocol>(protocols[0]?.id ?? 'rtmps')
  const pp = protocols.find((x) => x.id === protocol)
  const hls = protocol === 'hls'
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  // DS: PL-04 — refused by the plan (409 plan_limit_destinations, 422 plan_limit_source_grace): a link to the plan
  const [planErr, setPlanErr] = useState(false)
  const { user } = useSession()
  const keys = useQuery({ queryKey: ['stream-keys'], queryFn: () => get<StreamKey[]>('/api/stream-keys'), enabled: advanced })
  const ingest = useQuery({ queryKey: ['ingest'], queryFn: () => get<Ingest>('/api/ingest'), enabled: advanced })
  const keyGrace = keys.data?.find((k) => !k.revoked_at)?.source_grace_ms ?? null
  const inherited = keyGrace ?? ingest.data?.default_source_grace_ms
  const urlError = urlErrorFor(url, protocol)
  const steps = pp?.key_steps ?? profileSteps(p)
  const goLive = p.go_live_note
  const fromPlatform = serverFromPlatform(p)
  const invalid = !url.trim() || !!urlError || (grace !== null && graceBad)
  const domain = domainOf(p.key_url)
  const n = name.trim() || label(p)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (invalid || busy) return
    setBusy(true)
    setErr('')
    const body: DestinationInput = { name: n, profile: p.id, url: url.trim(), enabled: true }
    if (protocol !== (protocols[0]?.id ?? 'rtmps')) body.protocol = protocol
    if (key.trim()) body.key = key.trim()
    if (grace !== null) body.source_grace_ms = grace
    try {
      await onCreate(body)
    } catch (e2) {
      const plan = e2 instanceof ApiError ? refusalText(e2.code, user, e2.data) : undefined
      setPlanErr(!!plan)
      setErr(plan ?? (e2 as Error).message)
      setBusy(false)
    }
  }

  return (
    <form data-testid="add-form" data-profile={p.id} onSubmit={submit} aria-label={t('add.formLabel', { platform: label(p) })} className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <LogoSlot platform={platformOf(p.id)} />
        <span className="text-base font-semibold">{label(p)}</span>
        <PortraitTag p={p} />
      </div>
      {(!!steps?.length || p.key_url) && (
        <div data-testid="add-help" className="flex flex-col gap-2 rounded-md border border-line p-2.5">
          <span className="text-xs font-medium text-text-soft">{fromPlatform ? t('add.whereServerKey') : t('add.whereKey')}</span>
          {!!steps?.length && (
            <ol data-testid="add-steps" className="m-0 flex list-none flex-col gap-1.5 p-0 text-xs text-text-soft">
              {steps.map((s, i) => <li key={i} className="flex gap-2"><span className="font-mono font-semibold text-accent">{i + 1}</span><span>{s}</span></li>)}
            </ol>
          )}
          {p.key_url && (
            <a href={p.key_url} target="_blank" rel="noopener noreferrer" data-testid="add-key-url"
              className="inline-flex min-h-8 items-center self-start text-xs text-accent no-underline hover:underline max-md:min-h-[44px]">
              <span className="inline-flex items-center gap-1">{t('fix.key.open', { site: domain || p.name })}<Icon name="external" size={12} /></span>
            </a>
          )}
        </div>
      )}
      <Field label={t('add.name')} name="name" value={name} disabled={busy} onChange={setName} />
      <Field label={t('add.server')} name="url" value={url} mono placeholder={hls ? 'https://' : 'rtmp:// or rtmps://'} disabled={busy} error={urlError}
        hint={hls ? t('proto.serverHls') : fromPlatform ? t('add.serverFromPlatform', { platform: label(p) }) : p.url ? t('add.serverFilled') : t('add.serverAny')} onChange={(v) => { setUrl(v); setErr('') }} />
      <Field label={t('add.key')} name="key" value={key} type={shown ? 'text' : 'password'} mono autoComplete="off" disabled={busy}
        placeholder={t('fix.key.placeholder', { platform: label(p) })}
        hint={hls ? t('proto.keyNote') : p.needs_key ? (p.key_per_session ? t('add.keyLaterSession') : t('add.keyLater')) : t('add.keyOptional')}
        action={key ? { label: shown ? t('common.hide') : t('common.show'), onClick: () => setShown((v) => !v) } : undefined}
        onChange={setKey} />
      {goLive && <p data-testid="go-live-note" className="m-0 rounded-md border border-line px-2.5 py-2 text-xs text-text-soft">{goLive}</p>}
      <button type="button" data-testid="add-advanced" aria-expanded={advanced} onClick={() => setAdvanced((v) => !v)}
        className="sx-select w-full cursor-pointer text-left text-[13px] text-text-soft hover:text-text max-md:min-h-[44px]">
        <span>{t('add.advanced')}</span>
        <span aria-hidden className="inline-flex items-center gap-1 font-mono text-xs text-muted">{!advanced && hls ? 'HLS ' : ''}<Icon name="chevron-down" size={16} className={advanced ? 'rotate-180' : ''} /></span>
      </button>
      {advanced && protocols.length > 0 && (
        <ProtocolSettings testId="add-protocol" protocols={protocols} value={protocol} platformLabel={label(p)} name={n} live={live} disabled={busy}
          onChange={(v) => {
            // the server address follows the protocol unless the user typed their own
            const was = protocols.find((x) => x.id === protocol)?.url ?? p.url ?? ''
            const next = protocols.find((x) => x.id === v)?.url
            if (next && (!url.trim() || url.trim() === was)) setUrl(next)
            setProtocol(v)
            setErr('')
          }} />
      )}
      {advanced && (
        <GraceSettings testId="add-grace" value={grace} saved={null} inherited={inherited} inheritLabel="As the stream key"
          inheritFrom={keyGrace !== null ? 'the stream key’s' : 'the stream key’s (the default)'} subject={label(p)} live={live} disabled={busy}
          onChange={(v, bad) => { setGrace(v); setGraceBad(bad) }} />
      )}
      {err && <Banner tone="err">{err}</Banner>}
      {err && planErr && user?.plan && <Link to={BILLING_PATH} data-testid="add-plan" className="self-start text-xs text-accent no-underline">{t('plan.seePlan')} →</Link>}
      {!err && <Banner tone="info">{live ? t('add.liveNote', { name: n }) : t('add.offNote', { name: n })}</Banner>}
      <div className="grid grid-cols-2 gap-2">
        <SecondaryButton className="max-md:min-h-[44px]" disabled={busy} onClick={onCancel}>{t('common.back')}</SecondaryButton>
        <PrimaryButton type="submit" testId="add-submit" className="max-md:min-h-[44px]" busy={busy} disabled={invalid}>
          {busy ? t('add.adding') : live ? t('add.goLive') : t('add.submit')}
        </PrimaryButton>
      </div>
      <span className="text-center text-[11px] text-muted">{t('outs.limit', { n: count, max })}</span>
    </form>
  )
}
